import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input, Modal, Tag } from 'antd';
import { Lock, LockOpen, X } from 'lucide-react';
import moment from 'moment';
import styled from 'styled-components';

import { useLmsAccessBlock, useLmsBlockHistory } from 'core/hooks';
import type { ILmsBlockEvent } from 'core/hooks';
import { actorLabel, labelText, NS } from 'core/utils/lmsSuspension';

import { LmsAmountsDue, LmsBlockInvoices } from '../LmsBlockInvoices';
import { LmsBlockNotifications } from '../LmsBlockNotifications';
import { StudentSlackDeactivateHint } from '../StudentSlackLinks';

export type ILmsBlockModalMode = 'block' | 'unblock';

export interface ILmsBlockModalValues {
  /** Block only: optional free-text reason. */
  reason?: string;
  /**
   * Email + Slack DM + SMS to the student (ticked by default) — only the
   * channels switched on in the LMS-access settings (ТЗ 0074 / 0081).
   */
  notifyStudent: boolean;
}

interface IProps {
  mode: ILmsBlockModalMode;
  /**
   * The student the dialog is about. With it the dialog shows the current
   * suspension (source, date, invoices) and the history; without it — only
   * the form.
   */
  studentId?: string;
  loading?: boolean;
  onSubmit: (values: ILmsBlockModalValues) => void;
  onCancel: () => void;
}

const HISTORY_PREVIEW = 5;

const formatDateTime = (value?: string | null): string =>
  value ? moment(value).format('DD.MM.YYYY HH:mm') : '—';

/**
 * Shared LMS-access suspend / restore dialog (spec 0003 → ТЗ 0074). It never
 * calls a mutation itself — every call site (card header, command palette,
 * group roster, group debtors) drives its own hook through `onSubmit`. It does
 * READ the student's suspension and history, so all of them show the same
 * facts: who suspended and when, which invoices, what the student was sent.
 */
export const LmsBlockModal = memo<IProps>(
  ({ mode, studentId, loading, onSubmit, onCancel }) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [notifyStudent, setNotifyStudent] = useState(true);

    const isBlock = mode === 'block';

    const onOk = useCallback(() => {
      onSubmit({
        reason: isBlock ? reason.trim() || undefined : undefined,
        notifyStudent,
      });
    }, [isBlock, reason, notifyStudent, onSubmit]);

    return (
      <Modal
        visible
        centered
        title={
          isBlock
            ? t('pages.studentDetail.lmsBlock.block')
            : t('pages.studentDetail.lmsBlock.unblock')
        }
        onOk={onOk}
        onCancel={onCancel}
        confirmLoading={loading}
        okText={
          <ButtonLabel>
            {isBlock ? (
              <Lock size={14} strokeWidth={2} />
            ) : (
              <LockOpen size={14} strokeWidth={2} />
            )}
            {isBlock
              ? t('pages.studentDetail.lmsBlock.blockConfirm')
              : t('pages.studentDetail.lmsBlock.unblockConfirm')}
          </ButtonLabel>
        }
        cancelText={
          <ButtonLabel>
            <X size={14} strokeWidth={2} />
            {t('button.cancel')}
          </ButtonLabel>
        }
        okButtonProps={{ danger: isBlock }}
        destroyOnClose
        width={620}
      >
        {isBlock ? (
          <>
            <Notice>{t('pages.studentDetail.lmsBlock.blockExplain')}</Notice>
            <Label>{t('pages.studentDetail.lmsBlock.reasonPrompt')}</Label>
            <Input.TextArea
              autoFocus
              rows={3}
              maxLength={500}
              value={reason}
              placeholder={t('pages.studentDetail.lmsBlock.reasonPlaceholder')}
              onChange={(e) => setReason(e.target.value)}
            />
            {/* #54: блокировка закрывает LMS, но не Slack групп — там
                студента деактивируют вручную. Ссылки — только ролям,
                которым сервер отдаёт Slack студента. */}
            {studentId ? (
              <StudentSlackDeactivateHint studentId={studentId} />
            ) : null}
          </>
        ) : (
          <>
            {studentId ? <CurrentSuspension studentId={studentId} /> : null}
            <Notice>{t('pages.studentDetail.lmsBlock.unblockExplain')}</Notice>
          </>
        )}

        <NotifyRow>
          <Checkbox
            checked={notifyStudent}
            onChange={(e) => setNotifyStudent(e.target.checked)}
          >
            {t('pages.studentDetail.lmsBlock.notifyStudent')}
          </Checkbox>
          <Hint>{t('pages.studentDetail.lmsBlock.notifyStudentHint')}</Hint>
        </NotifyRow>

        {studentId ? <History studentId={studentId} /> : null}
      </Modal>
    );
  },
);

/** Who suspended, since when, and the invoices it is tied to. */
const CurrentSuspension = memo(({ studentId }: { studentId: string }) => {
  const { t } = useTranslation();
  const block = useLmsAccessBlock(studentId);

  if (!block.isBlocked) return null;

  return (
    <Facts>
      <Fact>
        <FactLabel>{t('pages.studentDetail.lmsBlock.suspendedBy')}</FactLabel>
        <ActorText source={block.source} name={block.blockedByName} />
      </Fact>
      <Fact>
        <FactLabel>{t('pages.studentDetail.lmsBlock.suspendedAt')}</FactLabel>
        <span>{formatDateTime(block.blockedAt)}</span>
      </Fact>
      {block.reason ? (
        <Fact>
          <FactLabel>{t('pages.studentDetail.lmsBlock.reason')}</FactLabel>
          <span>{block.reason}</span>
        </Fact>
      ) : null}
      <Fact>
        <FactLabel>{t(`${NS}.col.invoices`)}</FactLabel>
        <LmsBlockInvoices invoices={block.blockingInvoices} />
      </Fact>
      <Fact>
        <FactLabel>{t(`${NS}.col.amountsDue`)}</FactLabel>
        <LmsAmountsDue amounts={block.amountsDue} />
      </Fact>
    </Facts>
  );
});

/** «Robot» / a name; a pre-0074 legacy block is a warning tag. */
const ActorText = memo(
  ({
    source,
    name,
    muted,
  }: {
    source?: string | null;
    name?: string | null;
    muted?: boolean;
  }) => {
    const { t } = useTranslation();
    const text = labelText(actorLabel(source, name), t);
    if (source === 'legacy') return <Tag color="orange">{text}</Tag>;
    return muted ? <Hint>{text}</Hint> : <span>{text}</span>;
  },
);

/** The student's suspensions and restorations, newest first. */
const History = memo(({ studentId }: { studentId: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { events, loading, error } = useLmsBlockHistory(studentId);

  const visible = expanded ? events : events.slice(0, HISTORY_PREVIEW);

  return (
    <HistoryBox>
      <HistoryTitle>
        {t('pages.studentDetail.lmsBlock.history.title')}
        {events.length ? ` (${events.length})` : ''}
      </HistoryTitle>
      {loading ? (
        <Hint>{t('pages.studentDetail.lmsBlock.history.loading')}</Hint>
      ) : error ? (
        <Hint>{t('pages.studentDetail.lmsBlock.history.error')}</Hint>
      ) : !events.length ? (
        <Hint>{t('pages.studentDetail.lmsBlock.history.empty')}</Hint>
      ) : (
        <>
          {visible.map((event) => (
            <HistoryEvent key={event.id} event={event} />
          ))}
          {events.length > HISTORY_PREVIEW ? (
            <LinkButton type="button" onClick={() => setExpanded((v) => !v)}>
              {expanded
                ? t('pages.studentDetail.lmsBlock.history.showLess')
                : t('pages.studentDetail.lmsBlock.history.showAll', {
                    n: events.length,
                  })}
            </LinkButton>
          ) : null}
        </>
      )}
    </HistoryBox>
  );
});

const HistoryEvent = memo(({ event }: { event: ILmsBlockEvent }) => {
  const { t } = useTranslation();
  const suspended = event.action === 'suspended';
  return (
    <EventRow>
      <EventHead>
        <Tag color={suspended ? 'red' : 'green'}>
          {suspended
            ? t('pages.studentDetail.lmsBlock.history.suspended')
            : t('pages.studentDetail.lmsBlock.history.restored')}
        </Tag>
        <span>{formatDateTime(event.createdAt)}</span>
        <ActorText source={event.source} name={event.actorName} muted />
        <Spacer />
        <LmsBlockNotifications
          notifications={event.notifications}
          notifyStudent={event.notifyStudent}
        />
      </EventHead>
      {event.invoices.length ? (
        <LmsBlockInvoices invoices={event.invoices} compact />
      ) : null}
      {event.reason ? <Hint>{event.reason}</Hint> : null}
    </EventRow>
  );
});

const Notice = styled.div`
  font-size: 12px;
  color: var(--k-fg-9a3412);
  background: var(--k-bg-fff7ed);
  border: 1px solid var(--k-border-fed7aa);
  border-radius: 8px;
  padding: 8px 10px;
  line-height: 1.45;
  margin-bottom: 12px;
`;

const Label = styled.div`
  margin-bottom: 8px;
`;

const NotifyRow = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/**
 * Icon + text inside the antd footer buttons. `&&` — antd's own
 * `.ant-btn > span { display: inline-block }` outranks a single class.
 */
const ButtonLabel = styled.span`
  && {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: var(--k-fg-64748b);
`;

const Facts = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;
  row-gap: 6px;
  margin-bottom: 12px;
  font-size: 13px;
`;

const Fact = styled.div`
  display: contents;
`;

const FactLabel = styled.span`
  color: var(--k-fg-64748b);
  font-size: 12px;
  padding-top: 2px;
`;

const HistoryBox = styled.div`
  margin-top: 18px;
  padding-top: 12px;
  border-top: 1px solid var(--k-border-e2e8f0);
`;

const HistoryTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-475569);
  margin-bottom: 8px;
`;

const EventRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0;
  border-bottom: 1px dashed var(--k-border-e2e8f0);

  &:last-of-type {
    border-bottom: none;
  }
`;

const EventHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;

  .ant-tag {
    margin-right: 0;
  }
`;

const Spacer = styled.span`
  flex: 1;
`;

const LinkButton = styled.button`
  margin-top: 6px;
  padding: 0;
  border: none;
  background: none;
  color: var(--k-fg-2563eb);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
